import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Moon, Sun, Languages, Package } from "lucide-react";
import { useLang } from "@/i18n";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtIDR } from "@/lib/format";

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/exchange-rate").then(({ data }) => setRate(data.rate)).catch(() => {});
  }, []);

  const links = [
    { to: "/", label: t("nav_trips") },
    { to: "/book", label: t("nav_book") },
    { to: "/track", label: t("nav_track") },
    { to: "/admin", label: t("nav_admin") },
  ];

  return (
    <header className="sticky top-0 z-50 glass border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
          <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground grid place-items-center">
            <Package className="h-5 w-5" />
          </div>
          <span className="font-display font-extrabold text-lg tracking-tight hidden sm:block">{t("brand")}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {rate != null && (
            <span data-testid="rate-ticker" className="hidden lg:flex items-center gap-1 text-xs font-mono px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground">
              £1 = {fmtIDR(rate)}
            </span>
          )}
          <Button variant="ghost" size="icon" data-testid="lang-toggle" onClick={toggle} title="Language">
            <Languages className="h-4 w-4" />
            <span className="ml-1 text-xs font-bold uppercase">{lang}</span>
          </Button>
          <Button variant="ghost" size="icon" data-testid="theme-toggle" onClick={toggleTheme} title="Theme">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button className="hidden sm:inline-flex" data-testid="header-book-cta" onClick={() => navigate("/book")}>
            {t("cta_book")}
          </Button>
          <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn" onClick={() => setOpen(!open)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-card px-4 py-3 space-y-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
              className="block px-3 py-2 rounded-lg text-sm font-medium hover:bg-accent">
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};
